import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

export type ThemeScale = {
  50: string;
  100: string;
  200: string;
  300: string;
  400: string;
  500: string;
  600: string;
  700: string;
  800: string;
  900: string;
};

const THEME_STEPS: Array<keyof ThemeScale> = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];

export interface ClientTheme {
  id: string;
  name: string;
  primary: ThemeScale;
  semantic?: {
    bg?: string;
    surface?: string;
    text?: string;
    mutedText?: string;
    border?: string;
  };
}

const defaultTheme: ClientTheme = {
  id: 'flocksta-default',
  name: 'Flocksta Default',
  primary: {
    50: '#E8F7F3',
    100: '#D1F0E7',
    200: '#A3E1CF',
    300: '#5DCDB0',
    400: '#1AB894',
    500: '#0A9B80',
    600: '#088A72',
    700: '#066E5B',
    800: '#055346',
    900: '#033A30',
  },
  semantic: {
    bg: '#F6F5F2',
    surface: '#FFFFFF',
    text: '#1A1A1F',
    mutedText: '#5C5C6B',
    border: '#E4E4DE',
  },
};

interface ThemeContextValue {
  theme: ClientTheme;
  setTheme: (theme: ClientTheme) => void;
  resetTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  setTheme: () => {},
  resetTheme: () => {},
});

const hexToRgbChannels = (hex: string): string => {
  const value = hex.replace('#', '').trim();
  const normalized = value.length === 3
    ? value.split('').map((c) => `${c}${c}`).join('')
    : value;
  const int = parseInt(normalized, 16);

  if (Number.isNaN(int) || normalized.length !== 6) {
    return '0 0 0';
  }

  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `${r} ${g} ${b}`;
};

const applyThemeVariables = (theme: ClientTheme): void => {
  const root = document.documentElement;

  THEME_STEPS.forEach((step) => {
    root.style.setProperty(`--color-primary-${step}`, theme.primary[step]);
    root.style.setProperty(
      `--color-primary-${step}-rgb`,
      hexToRgbChannels(theme.primary[step])
    );
  });

  const bg = theme.semantic?.bg ?? defaultTheme.semantic?.bg ?? '#ffffff';
  const surface = theme.semantic?.surface ?? defaultTheme.semantic?.surface ?? '#f9fafb';
  const text = theme.semantic?.text ?? defaultTheme.semantic?.text ?? '#111827';
  const mutedText = theme.semantic?.mutedText ?? defaultTheme.semantic?.mutedText ?? '#4b5563';
  const border = theme.semantic?.border ?? defaultTheme.semantic?.border ?? '#e5e7eb';

  root.style.setProperty('--app-bg', bg);
  root.style.setProperty('--app-bg-rgb', hexToRgbChannels(bg));
  root.style.setProperty('--app-surface', surface);
  root.style.setProperty('--app-surface-rgb', hexToRgbChannels(surface));
  root.style.setProperty('--app-text', text);
  root.style.setProperty('--app-text-rgb', hexToRgbChannels(text));
  root.style.setProperty('--app-text-muted', mutedText);
  root.style.setProperty('--app-text-muted-rgb', hexToRgbChannels(mutedText));
  root.style.setProperty('--app-border', border);
  root.style.setProperty('--app-border-rgb', hexToRgbChannels(border));
};

const STORAGE_KEY = 'flocksta-client-theme';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ClientTheme>(() => {
    if (typeof window === 'undefined') {
      return defaultTheme;
    }
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return defaultTheme;
    }
    try {
      return JSON.parse(raw) as ClientTheme;
    } catch {
      return defaultTheme;
    }
  });

  useEffect(() => {
    applyThemeVariables(theme);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(theme));
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      setTheme: (nextTheme: ClientTheme) => setThemeState(nextTheme),
      resetTheme: () => setThemeState(defaultTheme),
    }),
    [theme]
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeContextValue => useContext(ThemeContext);

export const getDefaultTheme = (): ClientTheme => defaultTheme;
